<script setup>
import { computed, toRef, ref, watch } from 'vue';
import { getContrastingTextColor } from '@chatwoot/utils';
import CsWidgetButton from 'widget/v2/components/CsWidgetButton.vue';
import { IFrameHelper } from 'widget/helpers/utils';
import { CHATWOOT_ON_START_CONVERSATION } from 'widget/constants/sdkEvents';
import { CsAvatar } from 'dashboard/components/ui/commslayer';
import { useStore } from 'widget/composables/store';
import { useAvailability } from 'widget/composables/useAvailability';
import { animateEnter } from 'widget/composables/useEnterAnimation';
import { useI18n } from 'widget/composables/useI18n';

const MAX_DISPLAY_AGENTS = 3;
const STAGGER_DELAY = 50;

// Toggle this to enable/disable mock agents for testing
const USE_MOCK_AGENTS = false;

const MOCK_AGENTS = [
  {
    id: 1,
    name: 'John Doe',
    avatar: 'https://assets.dimrak.me/avatar/memoji/man-34.webp',
  },
  {
    id: 2,
    name: 'Jane Smith',
    avatar: 'https://assets.dimrak.me/avatar/memoji/woman-26.webp',
  },
  {
    id: 3,
    name: 'Sarah Wilson',
    avatar: 'https://assets.dimrak.me/avatar/memoji/woman-24.webp',
  },
  {
    id: 4,
    name: 'Mike Johnson',
    avatar: 'https://assets.dimrak.me/avatar/memoji/man-03.webp',
  },
];

// Props
const props = defineProps({
  availableAgents: {
    type: Array,
    default: () => [],
  },
  hasConversation: {
    type: Boolean,
    default: false,
  },
  unreadCount: {
    type: Number,
    default: 0,
  },
});

// Emits
const emit = defineEmits(['start-conversation']);

// Store
const store = useStore();
const { t } = useI18n();
const widgetColor = computed(() => store.getters['appConfig/getWidgetColor']);
const aiAgent = computed(() => store.getters['aiAgent/aiAgent']);

// Composables
const availableAgentsRef = toRef(props, 'availableAgents');
const { isOnline, replyWaitMessage } = useAvailability({
  availableAgents: availableAgentsRef,
});

// Computed
const textColor = computed(() => getContrastingTextColor(widgetColor.value));
const isAIEnabled = computed(() => aiAgent.value?.enabled || false);

// Use mock agents or real agents based on flag
const agents = computed(() =>
  USE_MOCK_AGENTS ? MOCK_AGENTS : props.availableAgents
);

const agentCountText = computed(() => {
  const count = agents.value?.length || 0;
  if (count === 0) return '';
  if (count === 1) return t('TEAM_AVAILABILITY.AGENT_COUNT.SINGULAR');
  return t('TEAM_AVAILABILITY.AGENT_COUNT.PLURAL', { count });
});

const displayAgents = computed(() =>
  agents.value.slice(0, MAX_DISPLAY_AGENTS).map(agent => ({
    id: agent.id,
    avatar: agent.avatar_url || agent.avatar,
    name: agent.name,
  }))
);

const remainingAgentsCount = computed(() =>
  Math.max(0, agents.value.length - MAX_DISPLAY_AGENTS)
);

// Animation refs
const avatarRefs = ref([]);
const statusRef = ref(null);
const descriptionRef = ref(null);
const buttonRef = ref(null);
const hasAnimated = ref(false);

// Watch for widget open to trigger animations
const isWidgetOpen = computed(() => store.getters['appConfig/getIsWidgetOpen']);

const runEnterAnimations = () => {
  let delay = 0;
  let delayAvatar = 0;

  // Animate each avatar
  [...avatarRefs.value].reverse().forEach(avatarEl => {
    if (avatarEl) {
      animateEnter(avatarEl, delayAvatar);
      delayAvatar += 20;
    }
  });

  // Animate status text
  if (statusRef.value) {
    animateEnter(statusRef.value, delay);
    delay += STAGGER_DELAY;
  }

  // Animate description
  if (descriptionRef.value) {
    animateEnter(descriptionRef.value, delay);
    delay += STAGGER_DELAY;
  }

  // Animate button
  if (buttonRef.value) {
    animateEnter(buttonRef.value, delay);
  }
};

watch(
  isWidgetOpen,
  (newVal, oldVal) => {
    if (newVal && !oldVal && !hasAnimated.value) {
      // Widget just opened
      // hasAnimated.value = true;
      runEnterAnimations();
    }
  },
  { immediate: true }
);

// Methods
const startConversation = () => {
  emit('start-conversation');
  if (!props.hasConversation) {
    IFrameHelper.sendMessage({
      event: 'onEvent',
      eventIdentifier: CHATWOOT_ON_START_CONVERSATION,
      data: { hasConversation: false },
    });
  }
};
</script>

<template>
  <section
    data-section="team-availability"
    data-component="TeamAvaibility"
    data-version="2"
    class="relative px-4 py-4 rounded-lg bg-white"
    style="box-shadow: 0 1.004px 3.012px 0 rgba(20, 4, 40, 0.08)"
  >
    <!-- Avatar overlap positioned at top right -->
    <div
      v-if="displayAgents.length > 0"
      class="absolute -top-5 right-3 flex items-center"
    >
      <div
        v-for="(agent, index) in displayAgents"
        :key="agent.id"
        :ref="
          el => {
            if (el) avatarRefs[index] = el;
          }
        "
      >
        <CsAvatar
          :name="agent.name"
          :size="42"
          variant="square"
          :src="agent.avatar"
          :class="[
            'border-2 border-solid border-white',
            index > 0 ? '-ml-2.5' : '',
          ]"
        />
      </div>
      <div
        v-if="remainingAgentsCount > 0"
        :ref="
          el => {
            if (el) avatarRefs[displayAgents.length] = el;
          }
        "
      >
        <CsAvatar
          :name="`+${remainingAgentsCount}`"
          :size="42"
          variant="square"
          class="-ml-2.5 border-2 border-solid border-white"
        />
      </div>
    </div>

    <div class="flex flex-col">
      <!-- Show if AI enabled -->
      <div v-if="isAIEnabled">
        <div ref="statusRef" class="flex items-center gap-2">
          <span class="h-2.5 w-2.5 rounded-full bg-green-500" />
          <span class="text-sm font-semibold text-slate-900">
            {{ $t('TEAM_AVAILABILITY.ONLINE') }}
          </span>
        </div>
        <div ref="descriptionRef" class="text-sm mt-1 text-slate-500">
          <span v-if="agents.length">{{ agentCountText }}.</span>
          {{ $t('REPLY_TIME.IN_A_FEW_MINUTES') }}
        </div>
      </div>

      <!-- Otherwise -->
      <div v-else>
        <div ref="statusRef" class="flex items-center gap-2">
          <span
            class="h-2.5 w-2.5 rounded-full"
            :class="isOnline ? 'bg-green-500' : 'bg-slate-400'"
          />
          <span class="text-sm font-semibold text-slate-900">
            {{
              isOnline
                ? $t('TEAM_AVAILABILITY.ONLINE')
                : $t('TEAM_AVAILABILITY.OFFLINE')
            }}
          </span>
        </div>
        <div ref="descriptionRef" class="text-sm mt-1 text-slate-500">
          {{ agentCountText }}. {{ replyWaitMessage }}
        </div>
      </div>
    </div>

    <div ref="buttonRef" class="mt-4 flex items-center gap-2">
      <CsWidgetButton
        size="lg"
        :bg-color="widgetColor"
        :text-color="textColor"
        @click="startConversation"
      >
        {{
          hasConversation
            ? $t('CONTINUE_CONVERSATION')
            : $t('START_CONVERSATION')
        }}
      </CsWidgetButton>
    </div>
  </section>
</template>
